
import React, { memo, forwardRef, useImperativeHandle, useMemo, useState, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import Modal from "@mui/material/Modal";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import * as yup from "yup";

import api from "services/api";
import ERRORS from "common/errors";
import { promptAlertMessage } from "store/slices/alert";
import validateFileSize from "common/validate-file-size";
import serveRequestErrors from "common/serve-request-errors";
import converReadableFileSize from "common/convert-readable-file-size";
import AppInput from "components/app-input";
import AppButton from "components/app-button";
import uploadIcon from "assets/images/upload-icon.svg";
import removeIcon from "assets/images/remove-icon.svg";
import AppCheckbox from "components/app-checkbox";
import COMMON from "common";

const ALLOWED_FILE_TYPES = ["image/jpeg", "application/pdf"];

export const AppCourseModal = (props, ref) => {
    const dispatch = useDispatch();
    const uploadInputRef = useRef();
    const [visible, setVisible] = useState(false);
    const [course, setCourse] = useState(null);
    const label = useMemo(() => (
        course ? "Edit Course" : "Create Course"
    ), [course])
    const buttonLabel = useMemo(() => (
        course ? "Save" : "Create"
    ), [course])
    const initialValues = useMemo(() => ({
        id: course?.id || "",
        title: course?.title || "",
        description: course?.description || "",
        isExamRequired: course?.isExamRequired || false,

        existingFiles: course?.files?.map((f) => ({
            id: f.id,
            name: f.fileName,
            size: f.fileSize,
            filePath: f.filePath,
        })) || [],

        newFiles: [],
        removedFileIds: [],
    }), [course]);
    
    //prettier-ignore
    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
        validationSchema: yup.object({
            title: yup.string().required(ERRORS.REQUIRED),
            newFiles: yup
                .array()
                .of(yup.mixed())
                .test("fileSize", ERRORS.FILE_SIZE_30, (files) => {
                    if (!files || files.length === 0) return true;
                    return validateFileSize(files, COMMON.MAX_FILE_SIZES.MB_30);
                })
                .test("fileType", ERRORS.FILE_TYPE_JPG_PDF, (files) => {
                    if (!files || files.length === 0) return true;
                    return files.every((file) => ALLOWED_FILE_TYPES.includes(file?.type));
                })
                .test("totalFileSize", ERRORS.TOTAL_FILE_SIZE_50, function (files) {
                    if (!files || files.length === 0) return true;
                    const existingSize = (this.parent.existingFiles || []).reduce((acc, file) => acc + (Number(file?.size) || 0), 0);
                    const newSize = files.reduce((acc, file) => acc + (file?.size || 0), 0);
                    return existingSize + newSize <= COMMON.MAX_FILE_SIZES.MB_50;
                })
        }),
        onSubmit: (values) => {
            onHandleConfirm(values);
        },
    });

    const onHandleShow = useCallback((obj) => {
        if (obj && obj.id) {
            setCourse(obj);
        }
        else {
            setCourse(null);
        };
        setVisible(true);
    }, []);

    const onHandleDismiss = useCallback(() => {
        formik.resetForm();
        setVisible(false);
    }, [formik]);

    const onHandleUploadFile = useCallback(() => {
        uploadInputRef.current.click();
    }, []);

    const handleRemoveExistingFile = useCallback((fileId) => {
        formik.setFieldValue(
            "existingFiles",
            formik.values.existingFiles.filter((f) => f.id !== fileId)
        );

        formik.setFieldValue(
            "removedFileIds",
            [...formik.values.removedFileIds, fileId]
        );
    }, [formik]);

    const handleRemoveNewFile = useCallback((indexToRemove) => {
        const updated = formik.values.newFiles.filter((_, index) => index !== indexToRemove);
        formik.setFieldValue("newFiles", updated);
    }, [formik]);

    //prettier-ignore
    const onHandleChange = useCallback((event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!ALLOWED_FILE_TYPES.includes(file.type)) {
            event.target.value = "";
            formik.setFieldTouched("newFiles", true, false);
            formik.setFieldError("newFiles", ERRORS.FILE_TYPE_JPG_PDF);
            return;
        }

        formik.setFieldValue("newFiles", [...formik.values.newFiles, file]);

        event.target.value = "";
    }, [formik]);

    const onHandleExamRequirement = useCallback((event) => {
        const name = event.target.name;
        const value = event.target.value;
        formik.setFieldValue(name, !value);
    }, [formik]);

    //prettier-ignore
    const onHandleUploadDocument = useCallback(async (values, courseId) => {
        if (!values.newFiles || values.newFiles.length === 0) {
            return true;
        }

        try {
            const formData = new FormData();
            values.newFiles.forEach((file) => {
                formData.append("files", file);
            });

            await api.post.course.upload(courseId, formData);
            return true;
        } catch (error) {
            serveRequestErrors(error);
            return false;
        } finally {
            uploadInputRef.current.value = ""
        }
    }, []);

    //prettier-ignore
    const onHandleRemoveDocument = useCallback(async (values) => {
        if (!values.removedFileIds || values.removedFileIds.length === 0) {
            return true;
        }

        try {
            await api.post.course.deleteFiles(values.removedFileIds);
            return true;
        } catch (error) {
            serveRequestErrors(error);
            return false;
        }
    }, []);

    //prettier-ignore
    const onHandleConfirm = useCallback(async (values) => {
        let response = false;

        try {
            const payload = {
                title: values.title,
                description: values.description,
                isExamRequired: values.isExamRequired
            };

            if (values.id) {
                payload.id = values.id;
                response = await api.post.course.update(payload);
            }
            else {
                response = await api.post.course.create(payload);
            }

            let uploadResponse = false;
            let removeResponse = false;
            if (response) {
                uploadResponse = await onHandleUploadDocument(values, response?.id);
                removeResponse = await onHandleRemoveDocument(values);
            }

            if (uploadResponse && removeResponse) {
                onHandleDismiss();
                dispatch(promptAlertMessage({ message: values.id ? "Course has been updated successfully" : "Course has been created successfully" }));
                props.onHandleGetList();
            }
        } catch (error) {
            serveRequestErrors(error);
        } finally {
            formik.setSubmitting(false);
        }
    }, [dispatch, formik, props, onHandleUploadDocument, onHandleDismiss, onHandleRemoveDocument]);

    const displayFiles = useMemo(() => {
        const existing = formik.values.existingFiles.map((f) => ({
            type: "existing",
            id: f.id,
            name: f.name,
            size: f.size,
            filePath: f.filePath,
        }));

        const news = formik.values.newFiles.map((file, index) => ({
            type: "new",
            index,
            name: file.name,
            size: file.size,
        }));

        // 👇 order is fixed here
        return [...existing, ...news];
    }, [formik.values.existingFiles, formik.values.newFiles]);

    //prettier-ignore
    useImperativeHandle(ref, () => ({
        onHandleShow: onHandleShow,
        onHandleDismiss: onHandleDismiss,
    }));

    return (
        <Modal classes={{ root: "app-course-modal" }} open={visible} onClose={onHandleDismiss} aria-labelledby="course-modal" aria-describedby="course-modal">
            <div className="course-modal">
                <div className="course-modal__main">
                    <div className="course-modal__header">
                        <p className="course-modal__title">{label}</p>
                    </div>

                    <form className="course-modal__body" onSubmit={formik.handleSubmit}>
                        {/* prettier-ignore */}
                        <AppInput required type="text" name="title" label="Title" placeholder="Title" value={formik.values.title} error={formik.errors.title} touched={formik.touched.title} disabled={formik.isSubmitting} onChange={formik.handleChange} />

                        {/* prettier-ignore */}
                        <div className="app-checkbox__label">
                            <AppCheckbox name="isExamRequired" label="Exam Required" onClick={onHandleExamRequirement} value={formik.values.isExamRequired} error={formik.errors.isExamRequired} />
                        </div>

                        {/* prettier-ignore */}
                        <AppInput multiline multilineRow={4} maxLength={255} type="text" name="description" label="Description" placeholder="Description" value={formik.values.description} error={formik.errors.description} touched={formik.touched.description} disabled={formik.isSubmitting} onChange={formik.handleChange} />

                        <div className="course-modal__upload-input">
                            {/* prettier-ignore */}
                            <label className="app-input__label" htmlFor="attachment">Attachment</label>
                            <p className="course-modal__description">Please ensure that your file is in the correct format (jpg, pdf) with each file size not exceeding 30MB and a total size not exceeding 50MB.</p>

                            <div className="course-modal__upload-body">
                                {displayFiles.map((file, index) => (
                                    <div className="course-modal__input" key={index}>
                                        {file.type === "existing" ? (
                                            <a
                                                href={file.filePath}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="course-modal__text course-modal__link"
                                            >
                                                {file.name}
                                            </a>
                                        ) : (
                                            <p className="course-modal__text">
                                                {file.name}
                                            </p>
                                        )}
                                        <p className="course-modal__text">{converReadableFileSize(file.size, true)}</p>
                                        {file.type === "existing" ? (
                                            <button
                                                type="button"
                                                className="course-modal__delete"
                                                onClick={() =>
                                                    handleRemoveExistingFile(file.id)
                                                }
                                            >
                                                <img src={removeIcon} alt="remove" />
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                className="course-modal__delete"
                                                onClick={() =>
                                                    handleRemoveNewFile(file.index)
                                                }
                                            >
                                                <img src={removeIcon} alt="remove" />
                                            </button>
                                        )}
                                    </div>
                                ))}

                                <div className="course-modal__input">
                                    <button type="button" className="course-modal__upload-button" onClick={onHandleUploadFile}>
                                        Upload
                                        <img className="course-modal__icon" src={uploadIcon} alt="upload" />
                                    </button>
                                </div>

                                <input type="file" name="file" accept="application/pdf,image/jpeg" hidden ref={uploadInputRef} onChange={onHandleChange} />
                            </div>
                        </div>

                        {formik.touched.newFiles && formik.errors.newFiles && <div className="course-modal__error">{formik.errors.newFiles}</div>}

                        <div className="course-modal__button-container">
                            <AppButton type="button" label="Cancel" outline onClick={onHandleDismiss} />
                            <AppButton type="submit" label={buttonLabel} disabled={formik.isSubmitting} />
                        </div>
                    </form>
                </div>
            </div >
        </Modal >
    );
};

export default memo(forwardRef(AppCourseModal));

AppCourseModal.propTypes = {
    ref: PropTypes.object,
};
